import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, CheckCircle2, FileSignature } from 'lucide-react';
import { toast } from 'sonner';
import { SignaturePad } from './SignaturePad';

/**
 * Generic Variation sign dialog. Used by both the authenticated portal
 * (passes `submitFn` that calls /portal/variations/{id}/sign) and the
 * public token flow (passes `submitFn` that calls /public/variations/{token}/sign).
 *
 * Props:
 *   open, onOpenChange — dialog state
 *   variation         — the variation summary object (id, filename, customer)
 *   defaultName       — pre-filled signer name (e.g. customer's full name)
 *   submitFn(signatureDataUrl, signedName) → Promise<{signed: true, ...}>
 *   onSigned          — callback after successful sign
 */
export const SignVariationDialog = ({
  open,
  onOpenChange,
  variation,
  defaultName = '',
  submitFn,
  onSigned,
}) => {
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [signedName, setSignedName] = useState(defaultName);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    if (open) {
      setSignedName(defaultName);
      setSignatureDataUrl('');
      setAgreed(false);
      setDone(false);
    }
  }, [open, defaultName]);

  const submit = async () => {
    if (!signatureDataUrl) { toast.error('Please draw your signature'); return; }
    if (!signedName.trim()) { toast.error('Please enter your full name'); return; }
    if (!agreed) {
      toast.error(isLoa
        ? 'Please confirm you authorise Helios to act on your behalf'
        : 'Please confirm you accept the variation terms');
      return;
    }
    setBusy(true);
    try {
      const r = await submitFn(signatureDataUrl, signedName.trim());
      setDone(true);
      toast.success(`${docLabel} signed — thank you!`);
      if (onSigned) onSigned(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit signature');
    } finally { setBusy(false); }
  };

  const isLoa = variation?.doc_tag === 'letter_of_authority';
  // UK spelling — see PublicSignVariation for context. The legal LoA doc
  // still uses "Authority" but the customer-facing UI uses "Authorisation".
  const docLabel = isLoa ? 'Letter of Authorisation' : 'Contract Variation';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sign-variation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSignature className="w-5 h-5 text-blue-600" /> Sign {docLabel}</DialogTitle>
          <DialogDescription>
            Please review the document and sign below. Your signature, name, IP address and timestamp will be recorded for audit.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="text-center py-6" data-testid="sign-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            <p className="font-semibold">Signed</p>
            <p className="text-sm text-slate-500 mt-1">A signed copy is now on file. The Helios team has been notified.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
              <p><strong>Document:</strong> {variation?.filename}</p>
              {!isLoa && variation?.variation_summary?.adjusted_total !== undefined && (
                <p><strong>New contract total:</strong> £{Number(variation.variation_summary.adjusted_total).toFixed(2)}</p>
              )}
              {!isLoa && variation?.variation_summary?.value_of_instruction !== undefined && (
                <p><strong>Net change:</strong> £{Number(variation.variation_summary.value_of_instruction).toFixed(2)}</p>
              )}
            </div>

            <div>
              <Label className="text-xs">Your full name</Label>
              <Input value={signedName} onChange={(e) => setSignedName(e.target.value)} placeholder="As shown on contract" data-testid="sign-name" />
            </div>

            <div>
              <Label className="text-xs">Signature</Label>
              <SignaturePad onChange={setSignatureDataUrl} dataTestId="sign-pad" />
            </div>

            <label className="flex items-start gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5"
                data-testid="sign-agreed"
              />
              <span>
                {isLoa
                  ? 'I confirm that I am the legal owner of the property at the address shown and that I authorise Helios Electrical & Solar to act on my behalf in the relevant DNO application.'
                  : 'I confirm I have read and accept the changes set out in this Variation of Contract.'}
              </span>
            </label>
          </div>
        )}

        <DialogFooter>
          {done ? (
            <Button onClick={() => onOpenChange(false)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sign-close">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sign-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSignature className="w-4 h-4 mr-1" />}
                Submit Signature
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignVariationDialog;
